import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Divider,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Text,
  tokens,
} from '@fluentui/react-components';
import LoadingIcon from './LoadingIcon';
import RedliningPalette from './RedliningPalette';
import CustomInstruction from './CustomInstruction';
import SuggestionList from './SuggestionList';
import FullReviewPrompt, { FullReviewAction } from './FullReviewPrompt';
import PartySelectPrompt from './PartySelectPrompt';
import { EditType, getEditType, gerundFor } from '../../services/editTypes';
import { AnalysisResult, ApplyOperation, Suggestion } from '../../services/analysis';
import { startFullReview } from '../../services/wordFullReview';
import { isAccountPlanDisabledError } from '../../services/planLimit';
import {
  createRedlineSession,
  detectParties,
  refineRewrite,
  triggerRewrite,
} from '../../services/wordRedlineSession';
import { AuthUser } from '../../services/auth';
import {
  applyReviewEdits,
  ApplyEditResult,
  enableTrackChanges,
  getFullReviewDocumentText,
  getSelectedText,
  isTrackChangesOn,
  selectByText,
} from '../../word/selection';

/* global Office, console */

interface RedliningTabProps {
  onApply: (rewrite: string, operation?: ApplyOperation) => void;
  pendingEditTypeId: string | null;
  onPendingHandled: () => void;
  user: AuthUser | null;
  onPlanLimitHit: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  intro: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },

  // ---- Full review (top of tab) ----
  reviewHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  reviewHeaderRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  reviewBlurb: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    flex: 1,
    minWidth: '160px',
  },
  reviewBox: {
    padding: '10px 12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  reviewLabel: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  reviewBody: {
    fontSize: tokens.fontSizeBase300,
  },
  reviewBreakdown: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  reviewActions: {
    display: 'flex',
    gap: '6px',
    marginTop: '2px',
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
  },
  divider: {
    marginTop: '2px',
    marginBottom: '2px',
  },
});

type ReviewState =
  | { phase: 'idle' }
  | { phase: 'uploading' }
  | {
      phase: 'streaming';
      chunksReceived: number;
      applied: number;
      skipped: number;
      elapsedSec: number;
    }
  | { phase: 'done'; result: Omit<ApplyEditResult, 'skips'> };

const RedliningTab: React.FC<RedliningTabProps> = ({
  onApply,
  pendingEditTypeId,
  onPendingHandled,
  user,
  onPlanLimitHit,
}) => {
  const styles = useStyles();

  // ---- Per-clause redlining state (existing) ----
  const [hasSelection, setHasSelection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [iterating, setIterating] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lockedOperation, setLockedOperation] = useState<ApplyOperation>('replace');
  const [activeEditType, setActiveEditType] = useState<EditType | null>(null);

  // ---- "Favor my side" party detection ----
  const [detectingParties, setDetectingParties] = useState(false);
  const [partyPrompt, setPartyPrompt] = useState<{
    editType: EditType;
    parties: string[];
    sessionId: string;
  } | null>(null);
  const [selectedParty, setSelectedParty] = useState<string | null>(null);

  // Redline session backing the current suggestion chain (signus-service).
  // Set on a fresh rewrite, reused by "Refine" iterations on that same result.
  const activeSessionIdRef = useRef<string | null>(null);

  // ---- Full-review state (moved from AnalysisTab) ----
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);
  const [review, setReview] = useState<ReviewState>({ phase: 'idle' });
  const [reviewError, setReviewError] = useState<string | null>(null);
  const reviewAbortRef = useRef<AbortController | null>(null);

  const refreshSelection = useCallback(async () => {
    try {
      const text = await getSelectedText();
      setHasSelection(text.trim().length > 0);
    } catch (e) {
      console.log('Failed to read selection:', e);
    }
  }, []);

  useEffect(() => {
    refreshSelection();
    const handler = () => refreshSelection();
    Office.context.document.addHandlerAsync(Office.EventType.DocumentSelectionChanged, handler);
    return () => {
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged, {
        handler,
      });
    };
  }, [refreshSelection]);

  const runAnalysis = useCallback(
    async (opts: {
      editType: EditType;
      customInstruction?: string;
      clauseTextOverride?: string;
      sessionId?: string;
      /** Overrides `selectedParty` state for this call only (avoids a stale-state race right after the party prompt resolves). */
      userParty?: string;
    }) => {
      const isIteration = opts.clauseTextOverride !== undefined;

      let clauseText: string;
      if (isIteration) {
        clauseText = opts.clauseTextOverride ?? '';
      } else {
        try {
          clauseText = (await getSelectedText()).trim();
        } catch {
          setError('Could not read the selection from Word.');
          return;
        }
        if (!clauseText) {
          return;
        }
      }

      setError(null);
      setLoading(true);
      setIterating(isIteration);
      setActiveEditType(opts.editType);
      if (!isIteration) setResult(null);

      try {
        let suggestion: Suggestion;

        if (!user) {
          throw new Error('You must be signed in to use redlining.');
        } else if (isIteration) {
          const sessionId = activeSessionIdRef.current;
          if (!sessionId) {
            throw new Error('No active redline session to refine — try the edit again.');
          }
          suggestion = await refineRewrite(user.teamSlug, user.accessToken, sessionId, {
            editTypeId: opts.editType.id,
          });
        } else {
          const sessionId =
            opts.sessionId ?? (await createRedlineSession(user.teamSlug, user.accessToken));
          activeSessionIdRef.current = sessionId;
          suggestion = await triggerRewrite(user.teamSlug, user.accessToken, sessionId, {
            editTypeId: opts.editType.id,
            clauseText,
            customInstruction: opts.customInstruction,
            userParty: opts.userParty ?? selectedParty ?? undefined,
          });
        }

        setResult({
          editType: opts.editType,
          clauseText,
          customInstruction: opts.customInstruction,
          suggestions: [suggestion],
        });
        if (!isIteration) {
          setLockedOperation(suggestion.operation ?? 'replace');
        }
      } catch (e) {
        if (isAccountPlanDisabledError(e)) {
          onPlanLimitHit();
        } else {
          setError(e instanceof Error ? e.message : 'Rewrite failed.');
        }
      } finally {
        setLoading(false);
        setIterating(false);
        setActiveEditType(null);
      }
    },
    [user, selectedParty, onPlanLimitHit],
  );

  const startPartyDetection = useCallback(
    async (editType: EditType) => {
      if (!user) {
        setError('You must be signed in to use Favor my side.');
        return;
      }

      setError(null);
      setDetectingParties(true);
      try {
        const sessionId = await createRedlineSession(user.teamSlug, user.accessToken);
        const detected = await detectParties(user.teamSlug, user.accessToken, sessionId);
        const parties = Array.from(new Set(detected.map(p => p.party).filter(Boolean)));

        if (parties.length === 0) {
          setError('Could not detect any parties in this document.');
          return;
        }

        setPartyPrompt({ editType, parties, sessionId });
      } catch (e) {
        if (isAccountPlanDisabledError(e)) {
          onPlanLimitHit();
        } else {
          setError(e instanceof Error ? e.message : 'Party detection failed.');
        }
      } finally {
        setDetectingParties(false);
      }
    },
    [user, onPlanLimitHit],
  );

  const handleEditTypeSelect = useCallback(
    (editType: EditType) => {
      if (editType.id === 'favor_my_side') {
        void startPartyDetection(editType);
        return;
      }
      runAnalysis({ editType });
    },
    [runAnalysis, startPartyDetection],
  );

  const handlePartyPromptResolve = useCallback(
    (party: string | null) => {
      const pendingEditType = partyPrompt?.editType ?? null;
      const sessionId = partyPrompt?.sessionId;
      setPartyPrompt(null);
      if (!party || !pendingEditType || !sessionId) return;
      setSelectedParty(party);
      runAnalysis({ editType: pendingEditType, sessionId, userParty: party });
    },
    [partyPrompt, runAnalysis],
  );

  const handleCustomSubmit = useCallback(
    (instruction: string) => {
      const customEditType: EditType = {
        id: 'custom',
        label: 'Custom',
        description: 'Free-text instruction',
        category: 'Negotiation',
        gerund: 'Generating',
      };
      runAnalysis({ editType: customEditType, customInstruction: instruction });
    },
    [runAnalysis],
  );

  const handleIterate = useCallback(
    (editType: EditType) => {
      const current = result?.suggestions[0]?.rewrite;
      if (!current) return;
      runAnalysis({ editType, clauseTextOverride: current });
    },
    [result, runAnalysis],
  );

  const handleScrollToReference = useCallback(async (snippet: string) => {
    try {
      await selectByText(snippet);
    } catch (e) {
      console.log('Could not scroll to reference:', e);
    }
  }, []);

  useEffect(() => {
    if (!pendingEditTypeId) return;
    const editType = getEditType(pendingEditTypeId);
    if (editType) {
      handleEditTypeSelect(editType);
    } else {
      setError(`Unknown edit type: ${pendingEditTypeId}`);
    }
    onPendingHandled();
  }, [pendingEditTypeId, handleEditTypeSelect, onPendingHandled]);

  // ---- Full-review flow ----
  const cancelReview = useCallback(() => {
    reviewAbortRef.current?.abort();
  }, []);

  const handleReviewResolve = useCallback(
    async (action: FullReviewAction) => {
      setShowReviewPrompt(false);
      if (action !== 'confirm') return;

      if (!user) {
        setReviewError('You must be signed in to run a full review.');
        return;
      }

      setReviewError(null);
      setReview({ phase: 'uploading' });

      const controller = new AbortController();
      reviewAbortRef.current = controller;
      const startedAt = Date.now();

      try {
        try {
          const tcOn = await isTrackChangesOn();
          if (!tcOn) await enableTrackChanges();
        } catch (e) {
          console.log("[review] couldn't toggle TC", e);
        }

        const docText = await getFullReviewDocumentText();

        const breakdown = { replace: 0, insert_after: 0, insert_before: 0, delete: 0 };
        let applied = 0;
        let skipped = 0;

        setReview({ phase: 'streaming', chunksReceived: 0, applied: 0, skipped: 0, elapsedSec: 0 });
        const elapsedTimer = setInterval(() => {
          setReview(prev => {
            if (prev.phase !== 'streaming') return prev;
            return { ...prev, elapsedSec: Math.floor((Date.now() - startedAt) / 1000) };
          });
        }, 1000);

        try {
          await startFullReview({
            teamSlug: user.teamSlug,
            accessToken: user.accessToken,
            signal: controller.signal,
            onChunk: async edit => {
              if (controller.signal.aborted) return;
              try {
                const result = await applyReviewEdits([edit], docText);
                applied += result.applied;
                skipped += result.skipped;
                if (result.applied > 0) breakdown[edit.type] += 1;
              } catch (e) {
                skipped++;
                console.log('[review] failed to apply edit', edit.find, e);
              }
              setReview(prev => {
                if (prev.phase !== 'streaming') return prev;
                return { ...prev, chunksReceived: prev.chunksReceived + 1, applied, skipped };
              });
            },
          });
        } finally {
          clearInterval(elapsedTimer);
        }

        if (controller.signal.aborted) {
          setReview({ phase: 'idle' });
          return;
        }

        setReview({
          phase: 'done',
          result: { applied, skipped, total: applied + skipped, breakdown },
        });
      } catch (e) {
        const name = (e as { name?: string } | null)?.name;
        if (name === 'AbortError' || controller.signal.aborted) {
          setReview({ phase: 'idle' });
          return;
        }
        console.log('[review] failed', e);
        setReview({ phase: 'idle' });
        if (isAccountPlanDisabledError(e)) {
          onPlanLimitHit();
        } else {
          setReviewError(e instanceof Error ? e.message : 'Review failed.');
        }
      } finally {
        reviewAbortRef.current = null;
      }
    },
    [user, onPlanLimitHit],
  );

  const reviewBusy = review.phase === 'uploading' || review.phase === 'streaming';

  const disablePalette = !hasSelection || loading || reviewBusy || detectingParties;
  const disableCustom = !hasSelection || loading || reviewBusy;
  const activeGerund = activeEditType ? gerundFor(activeEditType) : null;

  return (
    <div className={styles.root}>
      {/* ========= Full review (top of tab) ========= */}
      <div className={styles.reviewHeader}>
        <div className={styles.reviewHeaderRow}>
          <Button
            appearance="primary"
            onClick={() => setShowReviewPrompt(true)}
            disabled={reviewBusy || loading}
          >
            Full review
          </Button>
        </div>

        {reviewError && (
          <MessageBar intent="error">
            <MessageBarBody>{reviewError}</MessageBarBody>
          </MessageBar>
        )}

        {review.phase !== 'idle' && (
          <div className={styles.reviewBox}>
            <span className={styles.reviewLabel}>Full review</span>
            {review.phase === 'uploading' && (
              <span className={styles.loadingRow}>
                <LoadingIcon size={20} /> <span>Uploading document…</span>
              </span>
            )}
            {review.phase === 'streaming' && (
              <>
                <span className={styles.loadingRow}>
                  <LoadingIcon size={20} />
                  <span>
                    Sigi is reviewing the document
                    {review.chunksReceived > 0
                      ? ` · ${review.applied} edit${review.applied !== 1 ? 's' : ''} applied`
                      : ''}
                    {review.elapsedSec > 0 ? ` · ${review.elapsedSec}s` : ''}…
                  </span>
                </span>
                <div className={styles.reviewActions}>
                  <Button size="small" appearance="subtle" onClick={cancelReview}>
                    Cancel
                  </Button>
                </div>
              </>
            )}
            {review.phase === 'done' && (
              <>
                <div className={styles.reviewBody}>
                  {review.result.total === 0
                    ? 'No edits suggested — document looks clean.'
                    : `${review.result.applied} of ${review.result.total} edits applied as redlines.`}
                </div>
                {review.result.total > 0 && (
                  <div className={styles.reviewBreakdown}>
                    {review.result.breakdown.replace} replacements ·{' '}
                    {review.result.breakdown.insert_after + review.result.breakdown.insert_before}{' '}
                    insertions · {review.result.breakdown.delete} deletions
                    {review.result.skipped > 0 ? ` · ${review.result.skipped} skipped` : ''}
                  </div>
                )}
                <div className={styles.reviewActions}>
                  <Button
                    size="small"
                    appearance="subtle"
                    onClick={() => setReview({ phase: 'idle' })}
                  >
                    Dismiss
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <Divider className={styles.divider} />

      {/* ========= Per-clause redlining (existing) ========= */}
      <Text className={styles.intro}>Select a clause to rewrite.</Text>

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {detectingParties && (
        <span className={styles.loadingRow}>
          <LoadingIcon size={20} /> <span>Detecting parties…</span>
        </span>
      )}

      {selectedParty && (
        <Text className={styles.intro}>
          Favoring: <strong>{selectedParty}</strong>
        </Text>
      )}

      <RedliningPalette disabled={disablePalette} onSelect={handleEditTypeSelect} />
      <CustomInstruction disabled={disableCustom} onSubmit={handleCustomSubmit} />
      <SuggestionList
        loading={loading}
        iterating={iterating}
        activeGerund={activeGerund}
        result={result}
        lockedOperation={lockedOperation}
        onApply={onApply}
        onDismiss={() => setResult(null)}
        onIterate={handleIterate}
        onScrollToReference={handleScrollToReference}
      />

      <FullReviewPrompt open={showReviewPrompt} onResolve={handleReviewResolve} />
      <PartySelectPrompt
        open={!!partyPrompt}
        parties={partyPrompt?.parties ?? []}
        onResolve={handlePartyPromptResolve}
      />
    </div>
  );
};

export default RedliningTab;
